import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { observer } from 'mobx-react-lite';
import HeaderIcon from '../../assets/note.svg';
import RecordingStore from '../../store/RecordingStore';
import Page from '../base/Page';
import { colors } from '../const/Styles';
import { VideoBlob } from '../video/PlayingVideoCard';
import VideoComposer from '../video/VideoComposer';
import PlayerHeaderNavigation from './navigation/PlayerHeaderNavigation';
import { MultiplePlayMovieContext } from '../../contexts/MultiplePlayMovieContext';
import { Movie } from '../../domains/resource/movie';
import { useStore } from '../../contexts/StoreContext';
import { FooterButton } from '../video/VideoComposer/Footer';
import SaveSwingModal from './display/SaveSwingModal';

const ContentContainer = styled.section`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    height: 100%;
    width: 100%;
    background-color: ${colors.gray};
`;

const VideoContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    background-color: ${colors.gray};
    color: ${colors.white};
    .userName {
        color: ${colors.white};
    }
`;

type LocationProps = {
    customerId?: string;
    videoId?: number;
};

const PlaySwingPage: React.FC = () => {
    const location = useLocation<LocationProps>();
    const [saveCounter, setSaveCounter] = useState<number>(0);
    const { lessonStore } = useStore();
    const [modalOpened, setModalOpened] = useState(false);
    const { movies, multiplePlayMode, setMovies, setMultiplePlayMode } =
        useContext(MultiplePlayMovieContext);

    // propsの値がある場合は保持する
    if (location.state) {
        lessonStore.setLocationData(location.state);
    }
    const state = location.state ?? lessonStore.getLocationData();
    const customerId = state.customerId ?? '';
    const videoId = state.videoId ?? null;
    useEffect(() => {
        if (multiplePlayMode === false) {
            setMultiplePlayMode(true);
        }
        // 画面遷移時のpropsに値がある場合はstoreから値を検索し取得した後contextに保持する
        const videos = RecordingStore.getRegisteredVideo(customerId);
        const movie = videos?.find((v) => v.id === videoId);
        if (movie && movie.video && customerId) {
            // 保持しているidと異なる場合はcontextを更新する
            const isUpdate = !movies.some((movie) => movie.id === movie.id);
            if (isUpdate) {
                Movie.fromRawData(customerId, movie.video, movie)
                    .then((data) => {
                        // contextに保持して画面を再描画する
                        setMovies([data]);
                    })
                    .catch((error) => {
                        throw error;
                    });
            }
        }
    }, [
        movies,
        multiplePlayMode,
        customerId,
        videoId,
        setMovies,
        setMultiplePlayMode,
    ]);

    const videos = RecordingStore.getRegisteredVideo(customerId);
    const movie = videos?.find((v) => v.id === videoId);
    let videoBlob: VideoBlob | null = null;
    let toolimage = '';
    let controledToolMap = null;

    // 動画ファイルが変わらない限りURLを保持する
    const memoizedVideoUrl = useMemo(() => {
        if (movie && movie.video) {
            return URL.createObjectURL(movie.video);
        }
    }, [movie]);
    if (movie && movie.video && memoizedVideoUrl) {
        videoBlob = {
            id: movie.id,
            type: 'blob',
            videoSrc: memoizedVideoUrl,
            blob: movie.video,
            toolImage: movie.toolImage ?? undefined,
        };
        toolimage = RecordingStore.getToolImage(videoBlob);
        controledToolMap = {
            id: movie.id,
            video: movie.video,
            videoUrl: videoBlob.videoSrc,
            toolImage: videoBlob.toolImage,
        };
    }

    useEffect(() => {
        return () => {
            if (memoizedVideoUrl) {
                URL.revokeObjectURL(memoizedVideoUrl);
            }
        };
    }, [memoizedVideoUrl]);

    const handleOpenModal = () => {
        setModalOpened(true);
    };
    const handleCloseModal = () => {
        setModalOpened(false);
    };

    const extraButtons: FooterButton[] = [
        {
            label: 'スイング情報変更',
            onClick: () => {
                handleOpenModal();
            },
        },
    ];

    return (
        <Page title='レッスンノート' headerIcon={HeaderIcon ?? ''}>
            <ContentContainer>
                <PlayerHeaderNavigation customerId={customerId}>
                    スイング再生
                </PlayerHeaderNavigation>
                <VideoContainer>
                    {movie && videoBlob && (
                        <VideoComposer
                            isBackgroundDark={true}
                            ownerId={customerId}
                            movieSequence={parseInt(movie.number)}
                            club={movie.club}
                            yard={movie.yard}
                            isBestSwing={movie.isBestSwing}
                            toolImageFilePath={toolimage}
                            playbackPosition={movie.playbackPosition}
                            videoSrc={videoBlob}
                            height={601}
                            width={416}
                            saveCounter={saveCounter}
                            setSaveCounter={setSaveCounter}
                            extraButtons={extraButtons}
                        />
                    )}
                    {modalOpened && controledToolMap && (
                        <SaveSwingModal
                            onlyUpdate={true}
                            toolmap={controledToolMap}
                            onClose={handleCloseModal}
                        />
                    )}
                </VideoContainer>
            </ContentContainer>
        </Page>
    );
};

export default observer(PlaySwingPage);
